import { useState } from 'react';
import {
  Menu, Mail, Users, Calendar, ShieldCheck, Home, UserCog, Globe, Monitor, ScrollText, Search, X,
  HelpCircle, Settings, ArrowLeft, LogOut, ChevronDown, Moon, Sun,
} from 'lucide-react';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useTheme } from '../../context/ThemeContext';
import { getInitials } from '../../lib/avatar';

const LOGO_URL = '/netlen-logo.svg';

export const SECTIONS = [
  { id: 'overview', label: 'Genel bakış', icon: Home },
  { id: 'users', label: 'Kullanıcılar', icon: Users },
  { id: 'admins', label: 'Admin yönetimi', icon: UserCog },
  { id: 'ip', label: 'IP ayarları', icon: Globe },
  { id: 'sessions', label: 'Oturumlar', icon: Monitor },
  { id: 'logs', label: 'İşlem kayıtları', icon: ScrollText },
];

const RAIL = [
  { href: '/mail/inbox', label: 'E-posta', icon: Mail },
  { href: '/contacts', label: 'Kişiler', icon: Users },
  { href: '/calendar', label: 'Takvim', icon: Calendar },
];

const railClass = 'flex flex-col items-center gap-1 w-14 py-2.5 rounded-xl text-[11px] font-medium transition-colors';

export const AdminShell = ({ adminUser, section, onSection, userCount, searchQuery, onSearch, onLogout, children }) => {
  const [navOpen, setNavOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const name = adminUser?.username || 'Admin';
  const role = adminUser?.role === 'super_admin' ? 'Süper admin' : 'Yönetici';

  const navItems = (
    <>
      <p className="px-3 mb-2 text-[11px] font-semibold tracking-wider text-muted-foreground">YÖNETİM PANELİ</p>
      <div className="space-y-0.5">
        {SECTIONS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => { onSection(id); setNavOpen(false); }}
            aria-current={section === id ? 'page' : undefined}
            data-testid={`admin-nav-${id}`}
            className={`flex items-center gap-3 w-full px-3 h-10 rounded-lg text-sm transition-colors ${
              section === id ? 'bg-accent text-accent-foreground font-medium' : 'text-foreground/80 hover:bg-accent/60'
            }`}
          >
            <Icon className="h-[18px] w-[18px] shrink-0" />
            <span className="flex-1 text-left truncate">{label}</span>
            {id === 'users' && userCount != null && (
              <span className="min-w-[24px] h-5 px-1.5 rounded-full bg-primary/15 text-primary text-xs font-semibold flex items-center justify-center">{userCount}</span>
            )}
          </button>
        ))}
      </div>
    </>
  );

  const navFooter = (
    <div className="mt-auto">
      <div className="px-3 pb-3">
        <a href="/" className="flex items-center justify-center gap-2 h-10 rounded-lg border border-primary/60 text-primary text-sm font-medium hover:bg-accent/60" data-testid="back-to-webmail">
          <ArrowLeft className="h-4 w-4" /> Webmail'e dön
        </a>
      </div>
      <div className="px-4 py-3 border-t border-border flex items-center gap-3">
        <Avatar className="h-9 w-9"><AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">{getInitials(name, '')}</AvatarFallback></Avatar>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{name}</p>
          <p className="text-xs text-muted-foreground truncate">{role}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-background text-foreground" data-testid="admin-shell">
      <header className="h-16 shrink-0 flex items-center gap-3 px-3 sm:px-4 border-b border-border bg-background">
        <button onClick={() => setNavOpen((v) => !v)} className="lg:hidden h-10 w-10 rounded-lg hover:bg-secondary flex items-center justify-center" aria-label="Menüyü aç/kapat" data-testid="admin-menu-toggle">
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-3 shrink-0">
          <img src={LOGO_URL} alt="Netlen" className="h-7 w-auto dark:invert" onError={(e) => { e.target.style.display = 'none'; }} />
          <span className="hidden sm:block h-6 w-px bg-border" />
          <span className="hidden sm:block text-base text-muted-foreground">Webmail</span>
          <span className="hidden sm:inline-flex items-center rounded-full bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">Yönetim</span>
        </div>

        <div className="flex-1 flex justify-center min-w-0">
          <div className="relative w-full max-w-2xl">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Kullanıcı veya e-posta ara..."
              aria-label="Kullanıcı ara"
              className="w-full h-10 pl-10 pr-9 rounded-lg bg-secondary border border-transparent text-sm placeholder:text-muted-foreground focus:outline-none focus:bg-background focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
              data-testid="admin-search"
            />
            {searchQuery && (
              <button onClick={() => onSearch('')} aria-label="Aramayı temizle" className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <a href="/mail/inbox" className="hidden sm:inline-flex h-10 w-10 rounded-full hover:bg-secondary items-center justify-center" aria-label="Yardım" title="Yardım"><HelpCircle className="h-5 w-5" /></a>
          <button onClick={() => onSection('ip')} className="hidden sm:inline-flex h-10 w-10 rounded-full hover:bg-secondary items-center justify-center" aria-label="Yönetim ayarları" title="IP ayarları"><Settings className="h-5 w-5" /></button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="ml-1 flex items-center gap-2 rounded-full pr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label="Hesap menüsü" data-testid="admin-user-menu">
                <Avatar className="h-9 w-9"><AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">{getInitials(name, '')}</AvatarFallback></Avatar>
                <span className="hidden md:block text-left leading-tight">
                  <span className="block text-sm font-medium">{name}</span>
                  <span className="block text-xs text-muted-foreground">{role}</span>
                </span>
                <ChevronDown className="hidden md:block h-4 w-4 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal"><p className="text-sm font-medium truncate">{name}</p><p className="text-xs text-muted-foreground">{role}</p></DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={toggleTheme}>
                {theme === 'dark' ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
                {theme === 'dark' ? 'Açık tema' : 'Koyu tema'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onLogout} className="text-destructive" data-testid="admin-logout"><LogOut className="h-4 w-4 mr-2" />Çıkış yap</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="flex-1 min-h-0 flex">
        <nav className="hidden lg:flex w-[68px] shrink-0 bg-rail flex-col items-center py-3 gap-1" aria-label="Uygulama bölümleri">
          {RAIL.map(({ href, label, icon: Icon }) => (
            <a key={href} href={href} title={label} className={`${railClass} text-rail-foreground/70 hover:bg-rail-foreground/10 hover:text-rail-foreground`}>
              <Icon className="h-5 w-5" /><span className="leading-none">{label}</span>
            </a>
          ))}
          <span className={`${railClass} bg-rail-foreground/15 text-rail-foreground`} aria-current="page">
            <ShieldCheck className="h-5 w-5" /><span className="leading-none">Yönetim</span>
          </span>
        </nav>

        <aside className="hidden lg:flex w-[264px] shrink-0 bg-panel border-r border-border flex-col pt-5 px-3 min-h-0" data-testid="admin-sidenav">
          {navItems}
          {navFooter}
        </aside>

        {navOpen && (
          <>
            <div className="lg:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setNavOpen(false)} />
            <aside className="lg:hidden fixed inset-y-0 left-0 z-50 w-[280px] bg-panel flex flex-col pt-5 px-3 shadow-xl">{navItems}{navFooter}</aside>
          </>
        )}

        <main className="flex-1 min-w-0 min-h-0 overflow-y-auto bg-background" data-testid="admin-main">{children}</main>
      </div>
    </div>
  );
};

export default AdminShell;
